import * as THREE from "three";
import { Renderer } from "../core/renderer.js";

type MaterialMode = "BASIC" | "LAMBERT" | "PHONG" | "STANDARD";

const MATERIAL_MODES: MaterialMode[] = [ "BASIC", "LAMBERT", "PHONG", "STANDARD" ];

const COUNT_PRESETS = [ 10_000, 50_000, 100_000, 250_000, 500_000, 1_000_000, 2_000_000, 5_000_000 ];

const LIGHT_KINDS = [ "ambient", "hemisphere", "directional", "point", "spot" ] as const;
type LightKind = typeof LIGHT_KINDS[number];

const LIGHT_LABELS: Record<LightKind, string> = {
  ambient: "Ambient",
  hemisphere: "Hemisphere",
  directional: "Directional",
  point: "Point",
  spot: "Spot",
};

const TEXTURE_SIZES = [ 8, 16, 32, 64, 128, 256, 512, 1024, 2048 ];

const DEPTH_NEAR = 10;
const DEPTH_FAR = 34;
const FOV = 60;

function formatCount( n: number ): string
{
  if ( n >= 1_000_000 ) return `${( n / 1_000_000 ).toFixed( n % 1_000_000 === 0 ? 0 : 1 )}M`;
  if ( n >= 1_000 ) return `${( n / 1_000 ).toFixed( n % 1_000 === 0 ? 0 : 1 )}k`;
  return String( n );
}

export class TriangleBench
{
  private static active = false;

  private static scene: THREE.Scene | null = null;
  private static camera: THREE.PerspectiveCamera | null = null;
  private static mesh: THREE.Mesh | null = null;
  private static geometry: THREE.BufferGeometry | null = null;
  private static material: THREE.Material | null = null;

  private static lights: Partial<Record<LightKind, THREE.Light[]>> = {};
  private static shadows = false;

  private static count = 100_000;
  private static tri_size = 0.06;
  private static material_mode: MaterialMode = "BASIC";
  private static texture_size = 0;
  private static use_map = true;
  private static use_normal_map = false;
  private static use_emissive_map = false;
  private static spin_cos = { value: 1 };
  private static spin_sin = { value: 0 };
  private static color_texture: THREE.CanvasTexture | null = null;
  private static normal_texture: THREE.CanvasTexture | null = null;
  private static spin = false;

  private static raf = 0;
  private static last_time = 0;
  private static frames: number[] = [];
  private static angle = 0;

  private static context_lost = false;
  private static lost_events = 0;
  private static restored_events = 0;
  private static listeners_attached = false;
  private static collapsed = false;

  private static root: HTMLDivElement | null = null;
  private static readout: HTMLDivElement | null = null;
  private static hidden_canvases: HTMLCanvasElement[] = [];
  private static status = "";

  public static isActive(): boolean
  {
    return this.active;
  }

  public static setCount( n: number ): void
  {
    this.count = Math.max( 1, Math.round( n ) );
    this.rebuild();
  }

  public static setSize( s: number ): void
  {
    this.tri_size = s;
    this.rebuild();
  }

  public static setTexture( size: number ): void
  {
    const had_uv = this.needsUv();
    this.texture_size = size;
    this.disposeTextures();
    if ( size > 0 ) this.buildTextures();
    if ( had_uv !== this.needsUv() ) this.rebuild();
    else this.swapMaterial();
  }

  public static setSlot( slot: "map" | "normalMap" | "emissiveMap", on: boolean ): void
  {
    const had_uv = this.needsUv();
    if ( slot === "map" ) this.use_map = on;
    if ( slot === "normalMap" ) this.use_normal_map = on;
    if ( slot === "emissiveMap" ) this.use_emissive_map = on;
    if ( had_uv !== this.needsUv() ) this.rebuild();
    else this.swapMaterial();
  }

  private static swapMaterial(): void
  {
    this.material?.dispose();
    this.material = this.buildMaterial();
    if ( this.mesh ) this.mesh.material = this.material;
    this.frames = [];
    this.refreshPanel();
  }

  public static setMaterial( mode: MaterialMode ): void
  {
    if ( !MATERIAL_MODES.includes( mode ) ) return;
    const needed = this.needsNormals();
    this.material_mode = mode;
    if ( needed !== this.needsNormals() ) this.rebuild();
    else this.swapMaterial();
  }

  public static setSpin( on: boolean ): void
  {
    if ( this.spin === on ) return;
    this.spin = on;
    if ( this.mesh ) this.mesh.rotation.set( 0, 0, 0 );
    this.rebuild();
  }

  public static setShadows( on: boolean ): void
  {
    this.shadows = on;
    this.applyShadows();
    this.refreshPanel();
  }

  public static setLights( kind: LightKind, n: number ): number
  {
    while ( ( this.lights[kind]?.length ?? 0 ) > n ) this.removeLight( kind );
    while ( ( this.lights[kind]?.length ?? 0 ) < n ) this.addLight( kind );
    return this.lights[kind]?.length ?? 0;
  }

  public static resetStats(): void
  {
    this.frames = [];
  }

  public static isContextLost(): boolean
  {
    return this.context_lost || Renderer.renderer.getContext().isContextLost();
  }

  public static getStats(): Record<string, unknown>
  {
    const { fps, ms, p90 } = this.stats();
    const info = Renderer.renderer.info.render;
    const counts: Record<string, number> = {};
    for ( const kind of LIGHT_KINDS ) counts[kind] = this.lights[kind]?.length ?? 0;
    return {
      fps: +fps.toFixed( 2 ), ms: +ms.toFixed( 2 ), p90: +p90.toFixed( 2 ),
      samples: this.frames.length,
      count: this.count, size: this.tri_size, material: this.material_mode,
      texture: this.texture_size,
      slots: { map: this.use_map, normalMap: this.use_normal_map, emissiveMap: this.use_emissive_map },
      shadows: this.shadows, spin: this.spin, lights: counts,
      calls: info.calls, triangles: info.triangles, status: this.status,
      lost: this.isContextLost(),
      lost_events: this.lost_events, restored_events: this.restored_events,
      buffer_w: Renderer.renderer.domElement.width, buffer_h: Renderer.renderer.domElement.height,
      geo_tris: this.geometry ? ( this.geometry.getAttribute( "position" ) as THREE.BufferAttribute ).count / 3 : 0,
    };
  }

  public static open(): void
  {
    if ( this.active ) return;
    this.active = true;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color( 0x101418 );

    this.camera = new THREE.PerspectiveCamera( FOV, this.viewportAspect(), 0.1, 200 );
    this.camera.position.set( 0, 0, 0 );
    this.camera.lookAt( 0, 0, -1 );

    this.attachContextListeners();
    this.hideGameCanvases();
    this.rebuild();
    this.buildPanel();

    this.last_time = 0;
    this.frames = [];
    this.loop( performance.now() );
  }

  public static close(): void
  {
    if ( !this.active ) return;
    this.active = false;

    cancelAnimationFrame( this.raf );

    this.disposeMesh();
    this.clearLights();

    this.scene = null;
    this.camera = null;

    this.root?.remove();
    this.root = null;
    this.readout = null;

    for ( const canvas of this.hidden_canvases )
      canvas.style.display = "";
    this.hidden_canvases = [];
  }

  private static attachContextListeners(): void
  {
    if ( this.listeners_attached ) return;
    this.listeners_attached = true;

    const canvas = Renderer.renderer.domElement;
    canvas.addEventListener( "webglcontextlost", () =>
    {
      this.lost_events++;
      this.context_lost = true;
      this.status = `СОБЫТИЕ: контекст WebGL потерян (#${this.lost_events})`;
    } );
    canvas.addEventListener( "webglcontextrestored", () =>
    {
      this.restored_events++;
      this.status = `СОБЫТИЕ: контекст WebGL восстановлен (#${this.restored_events})`;
    } );
  }

  public static contextEvents(): Record<string, number | boolean>
  {
    return {
      lost_events: this.lost_events,
      restored_events: this.restored_events,
      latched: this.context_lost,
      now_lost: Renderer.renderer.getContext().isContextLost(),
    };
  }

  private static viewportAspect(): number
  {
    const size = Renderer.renderer.getSize( new THREE.Vector2() );
    return size.x > 0 && size.y > 0 ? size.x / size.y : 1;
  }

  public static viewportInfo(): Record<string, number>
  {
    const size = Renderer.renderer.getSize( new THREE.Vector2() );
    const canvas = Renderer.renderer.domElement;
    return {
      css_w: size.x, css_h: size.y,
      buffer_w: canvas.width, buffer_h: canvas.height,
      megapixels: +( canvas.width * canvas.height / 1e6 ).toFixed( 2 ),
      pixel_ratio: Renderer.renderer.getPixelRatio(),
    };
  }

  private static hideGameCanvases(): void
  {
    const gl_canvas = Renderer.renderer.domElement;
    for ( const canvas of Array.from( document.querySelectorAll( "canvas" ) ) )
    {
      if ( canvas === gl_canvas ) continue;
      if ( canvas.style.display === "none" ) continue;
      this.hidden_canvases.push( canvas as HTMLCanvasElement );
      ( canvas as HTMLCanvasElement ).style.display = "none";
    }
  }

  private static disposeMesh(): void
  {
    if ( this.mesh && this.scene ) this.scene.remove( this.mesh );
    this.geometry?.dispose();
    this.material?.dispose();
    this.mesh = null;
    this.geometry = null;
    this.material = null;
  }

  private static needsNormals(): boolean
  {
    return this.material_mode !== "BASIC";
  }

  private static needsCenters(): boolean
  {
    return this.spin;
  }

  private static needsUv(): boolean
  {
    return this.texture_size > 0 && ( this.use_map || this.use_normal_map || this.use_emissive_map );
  }

  private static disposeTextures(): void
  {
    this.color_texture?.dispose();
    this.normal_texture?.dispose();
    this.color_texture = null;
    this.normal_texture = null;
  }

  private static buildTextures(): void
  {
    this.disposeTextures();
    const size = this.texture_size;
    if ( size <= 0 ) return;

    const colour = document.createElement( "canvas" );
    colour.width = colour.height = size;
    const cctx = colour.getContext( "2d" )!;
    const cells = Math.max( 1, Math.min( size, 16 ) );
    const step = size / cells;
    for ( let y = 0; y < cells; y++ )
      for ( let x = 0; x < cells; x++ )
      {
        cctx.fillStyle = `hsl(${Math.floor( Math.random() * 360 )}, 70%, ${40 + Math.floor( Math.random() * 40 )}%)`;
        cctx.fillRect( x * step, y * step, step, step );
      }

    const normal = document.createElement( "canvas" );
    normal.width = normal.height = size;
    const nctx = normal.getContext( "2d" )!;
    const image = nctx.createImageData( size, size );
    for ( let i = 0; i < size * size; i++ )
    {
      image.data[i * 4] = 110 + Math.floor( Math.random() * 36 );
      image.data[i * 4 + 1] = 110 + Math.floor( Math.random() * 36 );
      image.data[i * 4 + 2] = 255;
      image.data[i * 4 + 3] = 255;
    }
    nctx.putImageData( image, 0, 0 );

    this.color_texture = new THREE.CanvasTexture( colour );
    this.color_texture.colorSpace = THREE.SRGBColorSpace;
    this.color_texture.wrapS = this.color_texture.wrapT = THREE.RepeatWrapping;
    this.color_texture.needsUpdate = true;

    this.normal_texture = new THREE.CanvasTexture( normal );
    this.normal_texture.wrapS = this.normal_texture.wrapT = THREE.RepeatWrapping;
    this.normal_texture.needsUpdate = true;
  }

  private static buildGeometry( count: number ): THREE.BufferGeometry | null
  {
    const with_normals = this.needsNormals();

    const with_uv = this.needsUv();
    const with_centers = this.needsCenters();

    let positions: Float32Array;
    let normals: Float32Array | null = null;
    let uvs: Float32Array | null = null;
    let centers: Float32Array | null = null;

    try
    {
      positions = new Float32Array( count * 9 );
      if ( with_normals ) normals = new Float32Array( count * 9 );
      if ( with_uv ) uvs = new Float32Array( count * 6 );
      if ( with_centers ) centers = new Float32Array( count * 9 );
    }
    catch ( error )
    {
      this.status = `не хватило памяти на ${formatCount( count )} треугольников (${( count * 9 * 4 * ( with_normals ? 2 : 1 ) / 1048576 ).toFixed( 0 )} МБ)`;
      return null;
    }

    const aspect = this.viewportAspect();
    if ( this.camera && this.camera.aspect !== aspect )
    {
      this.camera.aspect = aspect;
      this.camera.updateProjectionMatrix();
    }
    const tan_half = Math.tan( FOV * Math.PI / 360 );
    const size = this.tri_size;

    const ax = new THREE.Vector3(), bx = new THREE.Vector3(), nv = new THREE.Vector3();
    const p = [ new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3() ];

    for ( let i = 0; i < count; i++ )
    {
      const d = DEPTH_NEAR + Math.random() * ( DEPTH_FAR - DEPTH_NEAR );
      const half_h = tan_half * d;
      const half_w = half_h * aspect;
      const cx = ( Math.random() - 0.5 ) * 2 * Math.max( 0, half_w - size );
      const cy = ( Math.random() - 0.5 ) * 2 * Math.max( 0, half_h - size );
      const cz = -d;

      for ( let v = 0; v < 3; v++ )
      {
        p[v].set(
          cx + ( Math.random() - 0.5 ) * size,
          cy + ( Math.random() - 0.5 ) * size,
          cz + ( Math.random() - 0.5 ) * size
        );
        const o = i * 9 + v * 3;
        positions[o] = p[v].x;
        positions[o + 1] = p[v].y;
        positions[o + 2] = p[v].z;
      }

      if ( centers )
      {
        for ( let v = 0; v < 3; v++ )
        {
          const o = i * 9 + v * 3;
          centers[o] = cx;
          centers[o + 1] = cy;
          centers[o + 2] = cz;
        }
      }

      if ( uvs )
      {
        const u = i * 6;
        uvs[u] = 0; uvs[u + 1] = 0;
        uvs[u + 2] = 1; uvs[u + 3] = 0;
        uvs[u + 4] = 0.5; uvs[u + 5] = 1;
      }

      if ( normals )
      {
        ax.subVectors( p[1], p[0] );
        bx.subVectors( p[2], p[0] );
        nv.crossVectors( ax, bx );
        if ( nv.lengthSq() < 1e-12 ) nv.set( 0, 0, 1 ); else nv.normalize();
        for ( let v = 0; v < 3; v++ )
        {
          const o = i * 9 + v * 3;
          normals[o] = nv.x;
          normals[o + 1] = nv.y;
          normals[o + 2] = nv.z;
        }
      }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute( "position", new THREE.BufferAttribute( positions, 3 ) );
    if ( normals ) geometry.setAttribute( "normal", new THREE.BufferAttribute( normals, 3 ) );
    if ( uvs ) geometry.setAttribute( "uv", new THREE.BufferAttribute( uvs, 2 ) );
    if ( centers ) geometry.setAttribute( "aCenter", new THREE.BufferAttribute( centers, 3 ) );
    geometry.boundingSphere = new THREE.Sphere( new THREE.Vector3( 0, 0, -( DEPTH_NEAR + DEPTH_FAR ) / 2 ), DEPTH_FAR );
    return geometry;
  }

  private static buildMaterial(): THREE.Material
  {
    const textured = this.texture_size > 0;
    const options: Record<string, unknown> = {
      color: textured && this.use_map ? 0xffffff : 0x9fd8ff,
      side: THREE.DoubleSide,
      map: textured && this.use_map ? this.color_texture : null,
    };

    switch ( this.material_mode )
    {
      case "LAMBERT": return this.applySpinShader( new THREE.MeshLambertMaterial( { ...options, ...this.litSlots() } ) );
      case "PHONG": return this.applySpinShader( new THREE.MeshPhongMaterial( { ...options, ...this.litSlots() } ) );
      case "STANDARD": return this.applySpinShader( new THREE.MeshStandardMaterial( { ...options, ...this.litSlots(), roughness: 0.5, metalness: 0 } ) );
      default: return this.applySpinShader( new THREE.MeshBasicMaterial( options ) );
    }
  }

  private static applySpinShader( material: THREE.Material ): THREE.Material
  {
    if ( !this.spin ) return material;

    const u_cos = this.spin_cos;
    const u_sin = this.spin_sin;
    material.onBeforeCompile = ( shader ) =>
    {
      shader.uniforms.uSpinCos = u_cos;
      shader.uniforms.uSpinSin = u_sin;
      shader.vertexShader = shader.vertexShader
        .replace( "#include <common>",
          "#include <common>\nattribute vec3 aCenter;\nuniform float uSpinCos;\nuniform float uSpinSin;" )
        .replace( "#include <beginnormal_vertex>",
          [
            "vec3 objectNormal = vec3(",
            "  normal.x * uSpinCos - normal.y * uSpinSin,",
            "  normal.x * uSpinSin + normal.y * uSpinCos,",
            "  normal.z );",
          ].join( "\n" ) )
        .replace( "#include <begin_vertex>",
          [
            "vec3 spinOffset = position - aCenter;",
            "vec3 transformed = aCenter + vec3(",
            "  spinOffset.x * uSpinCos - spinOffset.y * uSpinSin,",
            "  spinOffset.x * uSpinSin + spinOffset.y * uSpinCos,",
            "  spinOffset.z );",
          ].join( "\n" ) );
    };
    material.customProgramCacheKey = () => "spin";
    material.needsUpdate = true;
    return material;
  }

  private static litSlots(): Record<string, unknown>
  {
    const textured = this.texture_size > 0;
    const slots: Record<string, unknown> = {};
    if ( textured && this.use_normal_map ) slots.normalMap = this.normal_texture;
    if ( textured && this.use_emissive_map )
    {
      slots.emissiveMap = this.color_texture;
      slots.emissive = new THREE.Color( 0xffffff );
      slots.emissiveIntensity = 0.5;
    }
    return slots;
  }

  private static rebuild(): void
  {
    if ( !this.scene ) return;

    const started = performance.now();
    if ( this.texture_size > 0 && !this.color_texture ) this.buildTextures();
    if ( this.texture_size <= 0 ) this.disposeTextures();
    const geometry = this.buildGeometry( this.count );
    if ( !geometry )
    {
      this.refreshPanel();
      return;
    }

    this.disposeMesh();

    this.geometry = geometry;
    this.material = this.buildMaterial();
    this.mesh = new THREE.Mesh( this.geometry, this.material );
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = this.shadows;
    this.mesh.receiveShadow = this.shadows;
    this.scene.add( this.mesh );

    const bytes = this.count * 9 * 4 * ( this.needsNormals() ? 2 : 1 ) + ( this.needsUv() ? this.count * 6 * 4 : 0 );
    this.status = `собрано за ${Math.round( performance.now() - started )} мс, буфер ${( bytes / 1048576 ).toFixed( 1 )} МБ`;
    this.frames = [];
    this.refreshPanel();
  }

  private static clearLights(): void
  {
    for ( const kind of LIGHT_KINDS )
    {
      for ( const light of this.lights[kind] ?? [] )
      {
        light.parent?.remove( light );
        ( light as any ).dispose?.();
      }
      this.lights[kind] = [];
    }
  }

  private static makeLight( kind: LightKind, index: number ): THREE.Light
  {
    const angle = index * 1.7;
    switch ( kind )
    {
      case "ambient":
        return new THREE.AmbientLight( 0xffffff, 0.4 );
      case "hemisphere":
        return new THREE.HemisphereLight( 0xdff1ff, 0x2a3f6b, 0.5 );
      case "point":
      {
        const light = new THREE.PointLight( 0xffd9a0, 40, 60, 2 );
        light.position.set( Math.cos( angle ) * 10, Math.sin( angle ) * 10, 8 );
        return light;
      }
      case "spot":
      {
        const light = new THREE.SpotLight( 0xffffff, 60, 80, Math.PI / 6, 0.3, 2 );
        light.position.set( Math.cos( angle ) * 12, Math.sin( angle ) * 12, 14 );
        return light;
      }
      default:
      {
        const light = new THREE.DirectionalLight( 0xffffff, 1.6 );
        light.position.set( Math.cos( angle ) * 10, Math.sin( angle ) * 10, 10 );
        return light;
      }
    }
  }

  private static addLight( kind: LightKind ): void
  {
    if ( !this.scene ) return;
    const list = this.lights[kind] ?? ( this.lights[kind] = [] );
    const light = this.makeLight( kind, list.length );

    if ( this.shadows && ( kind === "directional" || kind === "spot" || kind === "point" ) )
      light.castShadow = true;

    this.scene.add( light );
    list.push( light );
    this.frames = [];
    this.refreshPanel();
  }

  private static removeLight( kind: LightKind ): void
  {
    const list = this.lights[kind] ?? [];
    const light = list.pop();
    if ( !light ) return;
    light.parent?.remove( light );
    ( light as any ).dispose?.();
    this.frames = [];
    this.refreshPanel();
  }

  private static lightCount(): number
  {
    return LIGHT_KINDS.reduce( ( sum, kind ) => sum + ( this.lights[kind]?.length ?? 0 ), 0 );
  }

  private static applyShadows(): void
  {
    if ( this.mesh )
    {
      this.mesh.castShadow = this.shadows;
      this.mesh.receiveShadow = this.shadows;
    }
    for ( const kind of [ "directional", "spot", "point" ] as LightKind[] )
      for ( const light of this.lights[kind] ?? [] )
        light.castShadow = this.shadows;

    Renderer.renderer.shadowMap.enabled = this.shadows;
    this.frames = [];
  }

  private static loop = ( time: number ): void =>
  {
    if ( !this.active || !this.scene || !this.camera ) return;

    this.raf = requestAnimationFrame( this.loop );

    if ( this.last_time )
    {
      this.frames.push( time - this.last_time );
      if ( this.frames.length > 240 ) this.frames.shift();
    }
    this.last_time = time;

    if ( this.spin )
    {
      this.angle += 0.02;
      this.spin_cos.value = Math.cos( this.angle );
      this.spin_sin.value = Math.sin( this.angle );
    }

    if ( this.shadows )
      Renderer.renderer.shadowMap.needsUpdate = true;

    if ( !this.context_lost )
    {
      Renderer.renderer.render( this.scene, this.camera );

      if ( Renderer.renderer.getContext().isContextLost() )
      {
        this.context_lost = true;
        this.status = `КОНТЕКСТ WebGL ПОТЕРЯН на ${formatCount( this.count )} треугольников — это потолок устройства`;
        this.refreshPanel();
      }
    }

    this.updateReadout();
  };

  private static stats(): { fps: number; ms: number; p90: number }
  {
    if ( this.frames.length < 5 ) return { fps: 0, ms: 0, p90: 0 };
    const sum = this.frames.reduce( ( a, b ) => a + b, 0 );
    const sorted = this.frames.slice().sort( ( a, b ) => a - b );
    return {
      fps: 1000 * this.frames.length / sum,
      ms: sum / this.frames.length,
      p90: sorted[Math.floor( sorted.length * 0.9 )],
    };
  }

  private static updateReadout(): void
  {
    if ( !this.readout ) return;
    const { fps, ms, p90 } = this.stats();
    const info = Renderer.renderer.info.render;
    this.readout.innerHTML =
      `<b style="font-size:22px">${fps.toFixed( 1 )} fps</b> &nbsp; ${ms.toFixed( 1 )} мс &nbsp; p90 ${p90.toFixed( 1 )}<br>` +
      `${formatCount( this.count )} тр · размер ${this.tri_size} · ${this.material_mode} · света ${this.lightCount()} · ` +
      `текстура ${this.texture_size > 0 ? `${this.texture_size}x${this.texture_size} [${[ this.use_map ? "map" : "", this.use_normal_map ? "normal" : "", this.use_emissive_map ? "emissive" : "" ].filter( Boolean ).join( "+" ) || "нет слотов"}]` : "выкл"}<br>` +
      `<span style="opacity:.7">буфер ${Renderer.renderer.domElement.width}x${Renderer.renderer.domElement.height} · вызовов ${info.calls} · в кадре ${info.triangles.toLocaleString( "ru" )} из ${this.count.toLocaleString( "ru" )} · ${this.status}</span>`;
  }

  private static button( text: string, action: () => void, active = false ): HTMLButtonElement
  {
    const el = document.createElement( "button" );
    el.type = "button";
    el.textContent = text;
    el.style.minHeight = "44px";
    el.style.padding = "0 12px";
    el.style.fontSize = "13px";
    el.style.fontWeight = "700";
    el.style.color = "#fff";
    el.style.background = active ? "#1e88e5" : "#37474f";
    el.style.border = "none";
    el.style.borderRadius = "8px";
    el.addEventListener( "click", action );
    return el;
  }

  private static row(): HTMLDivElement
  {
    const el = document.createElement( "div" );
    el.style.display = "flex";
    el.style.flexWrap = "wrap";
    el.style.gap = "6px";
    el.style.alignItems = "center";
    el.style.marginBottom = "8px";
    return el;
  }

  private static label( text: string ): HTMLDivElement
  {
    const el = document.createElement( "div" );
    el.textContent = text;
    el.style.width = "100%";
    el.style.fontSize = "11px";
    el.style.letterSpacing = "0.08em";
    el.style.opacity = "0.6";
    el.style.margin = "2px 0";
    return el;
  }

  private static refreshPanel(): void
  {
    if ( this.root ) this.buildPanel();
  }

  private static buildPanel(): void
  {
    this.root?.remove();

    const root = document.createElement( "div" );
    root.id = "triangleBenchPanel";
    root.style.position = "fixed";
    root.style.left = "0";
    root.style.right = "0";
    root.style.bottom = "0";
    root.style.maxHeight = this.collapsed ? "none" : "62vh";
    root.style.overflowY = this.collapsed ? "visible" : "auto";
    root.style.zIndex = "2147483600";
    root.style.background = "rgba(8, 10, 16, 0.92)";
    root.style.color = "#fff";
    root.style.font = "13px Arial, sans-serif";
    root.style.padding = "10px 10px 20px";
    root.style.boxSizing = "border-box";

    for ( const name of [ "pointerdown", "pointermove", "pointerup", "click", "touchstart", "touchmove" ] )
      root.addEventListener( name, e => e.stopPropagation() );

    const head = document.createElement( "div" );
    head.style.display = "flex";
    head.style.alignItems = "flex-start";
    head.style.gap = "8px";

    const readout = document.createElement( "div" );
    readout.style.flex = "1";
    readout.style.padding = "6px 4px 10px";
    readout.style.lineHeight = "1.5";
    head.appendChild( readout );
    head.appendChild( this.button( this.collapsed ? "▲" : "▼", () =>
    {
      this.collapsed = !this.collapsed;
      this.buildPanel();
    } ) );
    root.appendChild( head );

    if ( this.collapsed )
    {
      document.body.appendChild( root );
      this.root = root;
      this.readout = readout;
      this.updateReadout();
      return;
    }

    root.appendChild( this.label( "ТРЕУГОЛЬНИКОВ" ) );
    const counts = this.row();
    for ( const preset of COUNT_PRESETS )
      counts.appendChild( this.button( formatCount( preset ), () =>
      {
        this.count = preset;
        this.rebuild();
      }, this.count === preset ) );
    root.appendChild( counts );

    const slider_row = this.row();
    const slider = document.createElement( "input" );
    slider.type = "range";
    slider.min = "3";
    slider.max = "6.7";
    slider.step = "0.05";
    slider.value = String( Math.log10( this.count ) );
    slider.style.flex = "1";
    slider.style.minWidth = "180px";
    const slider_value = document.createElement( "span" );
    slider_value.style.minWidth = "60px";
    slider_value.textContent = formatCount( this.count );
    slider.addEventListener( "input", () =>
    {
      slider_value.textContent = formatCount( Math.round( Math.pow( 10, Number( slider.value ) ) ) );
    } );
    slider.addEventListener( "change", () =>
    {
      this.count = Math.round( Math.pow( 10, Number( slider.value ) ) );
      this.rebuild();
    } );
    slider_row.appendChild( slider );
    slider_row.appendChild( slider_value );
    root.appendChild( slider_row );

    root.appendChild( this.label( "РАЗМЕР ТРЕУГОЛЬНИКА" ) );
    const size_row = this.row();
    for ( const size of [ 0.01, 0.03, 0.06, 0.12, 0.25, 0.5 ] )
      size_row.appendChild( this.button( String( size ), () =>
      {
        this.tri_size = size;
        this.rebuild();
      }, this.tri_size === size ) );
    root.appendChild( size_row );

    root.appendChild( this.label( "ТЕКСТУРА" ) );
    const tex_row = this.row();
    tex_row.appendChild( this.button( "ВЫКЛ", () => this.setTexture( 0 ), this.texture_size === 0 ) );
    for ( const size of TEXTURE_SIZES )
      tex_row.appendChild( this.button( String( size ), () => this.setTexture( size ), this.texture_size === size ) );
    root.appendChild( tex_row );

    const slot_row = this.row();
    for ( const [ slot, label, on ] of [
      [ "map", "map", this.use_map ],
      [ "normalMap", "normalMap", this.use_normal_map ],
      [ "emissiveMap", "emissiveMap", this.use_emissive_map ],
    ] as [ "map" | "normalMap" | "emissiveMap", string, boolean ][] )
      slot_row.appendChild( this.button( `${label}: ${on ? "ON" : "OFF"}`, () => this.setSlot( slot, !on ), on ) );
    root.appendChild( slot_row );

    root.appendChild( this.label( "МАТЕРИАЛ  (normalMap и emissiveMap работают только на LAMBERT/PHONG/STANDARD)" ) );
    const mat_row = this.row();
    for ( const mode of MATERIAL_MODES )
      mat_row.appendChild( this.button( mode, () =>
      {
        const needed_normals = this.needsNormals();
        this.material_mode = mode;
        if ( needed_normals !== this.needsNormals() ) this.rebuild();
        else
        {
          this.material?.dispose();
          this.material = this.buildMaterial();
          if ( this.mesh ) this.mesh.material = this.material;
          this.frames = [];
          this.refreshPanel();
        }
      }, this.material_mode === mode ) );
    root.appendChild( mat_row );

    root.appendChild( this.label( "СВЕТ  (действует на всех кроме BASIC)" ) );
    for ( const kind of LIGHT_KINDS )
    {
      const line = this.row();
      const name = document.createElement( "span" );
      name.textContent = `${LIGHT_LABELS[kind]}: ${this.lights[kind]?.length ?? 0}`;
      name.style.minWidth = "120px";
      line.appendChild( name );
      line.appendChild( this.button( "+", () => this.addLight( kind ) ) );
      line.appendChild( this.button( "−", () => this.removeLight( kind ) ) );
      root.appendChild( line );
    }

    const extra = this.row();
    extra.appendChild( this.button( `ТЕНИ: ${this.shadows ? "ON" : "OFF"}`, () =>
    {
      this.shadows = !this.shadows;
      this.applyShadows();
      this.refreshPanel();
    }, this.shadows ) );
    extra.appendChild( this.button( `ВРАЩЕНИЕ НА МЕСТЕ: ${this.spin ? "ON" : "OFF"}`, () =>
    {
      this.setSpin( !this.spin );
    }, this.spin ) );
    extra.appendChild( this.button( "СБРОС СВЕТА", () =>
    {
      this.clearLights();
      this.refreshPanel();
    } ) );
    extra.appendChild( this.button( "ВЫХОД", () =>
    {
      this.close();
      window.location.reload();
    } ) );
    root.appendChild( extra );

    document.body.appendChild( root );
    this.root = root;
    this.readout = readout;
    this.updateReadout();
  }
}
